import type { ZipPackageGroup } from '@atlas/types'
import { FileJobStatus, FileJobType } from '@atlas/types'
import type { QueryClient } from '@tanstack/react-query'
import { apiErrorStatus } from '@/lib/api-error'
import { triggerBrowserDownload } from '@/lib/browser-download'
import { logger } from '@/lib/logger'
import { notification } from '@/notifications/notification-store'
import { apiClient } from './api.client'

// The only place that calls the four `/files/jobs*` routes. Every response is
// read defensively before anything downstream sees it, following
// `realtime/notification-bridge.ts`'s treatment of an untrusted payload: a
// drifted shape degrades to "nothing usable" rather than reaching a surface
// half-formed. No response field is trusted for being present, and no filename
// is ever constructed here — the backend owns it.

const log = logger.child('file-jobs')

/** Root of the File Job listing key family. One entry per job type; only `zip` exists today. */
export const FILE_JOB_LIST_KEY = 'file-jobs'

/** The one listing key every surface reads and every party invalidates to converge. */
export const zipJobListKey = () => [FILE_JOB_LIST_KEY, FileJobType.ZIP]

/** Query key for the authoritative per-request status read. */
export const fileJobRequestKey = (requestId: string) => ['file-job-request', requestId]

/** Statuses a request never moves out of — following it can stop once it reports one. */
export const TERMINAL_FILE_JOB_STATUSES: ReadonlySet<FileJobStatus> = new Set([
	FileJobStatus.READY,
	FileJobStatus.FAILED,
	FileJobStatus.EXPIRED,
])

/** One row of the useful-work listing, and the shape a per-request status read resolves to. */
export interface ZipRequestSummary {
	requestId: string
	jobId: string
	status: FileJobStatus
	fileName: string
	requestedCount?: number
	includedCount?: number
	unavailableCount?: number
	createdAt?: string
	completedAt?: string
	resultExpiresAt?: string
}

/** What `POST /files/jobs` returns — the shared job, this requester's request against it, and the job's current status. */
export interface ZipJobCreated {
	jobId: string
	requestId: string
	type: FileJobType
	status: FileJobStatus
}

/** A freshly minted download credential. Never stored, never returned to a caller — see {@link downloadZipResult}. */
export interface ZipResult {
	requestId: string
	downloadUrl: string
	fileName: string
	requestedCount: number
	includedCount: number
	unavailableCount: number
}

/** What a caller asks for: the Files, the folder each lands in, and the name the archive downloads as. */
export interface CreateFileJobBody {
	type: FileJobType
	idempotencyKey: string
	groups: ZipPackageGroup[]
	downloadFileName?: string
}

/** Both identities plus the SHARED job's current status, which may already be past QUEUED when equivalent work converges. */
export interface FileJobCreated {
	jobId: string
	requestId: string
	type: FileJobType
	status: FileJobStatus
}

/**
 * One request and its shared job's status.
 *
 * The counts are absent until the job is READY — they are written at completion,
 * so a queued request legitimately reports none.
 */
export interface FileJobRequestSummary {
	requestId: string
	jobId: string
	type: FileJobType
	status: FileJobStatus
	fileName: string
	requestedCount?: number
	includedCount?: number
	unavailableCount?: number
	createdAt: string
	completedAt?: string
	resultExpiresAt?: string
}

/** A fresh download credential. Minted per call and short-lived, so it is never cached. */
export interface FileJobResult {
	requestId: string
	jobId: string
	downloadUrl: string
	fileName: string
	requestedCount: number
	includedCount: number
	unavailableCount: number
	resultExpiresAt: string
	downloadUrlExpiresAt: string
}

/**
 * The three genuine outcomes of reading one request's status.
 *
 * A transient failure is deliberately NOT a member: it stays a rejected
 * promise, so a caller can never mistake "the read failed" for "the backend
 * answered", and never has to inspect a value to tell them apart.
 */
export type FileJobStatusRead =
	| { kind: 'found'; summary: ZipRequestSummary }
	| { kind: 'not-found' }
	| { kind: 'unusable' }

/** A JSON object, as far as anything can be told before its fields are read. */
function asRecord(raw: unknown): Record<string, unknown> | null {
	return typeof raw === 'object' && raw !== null && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null
}

/** A required, non-empty string field; `null` when absent, empty, or another type. */
function requiredString(record: Record<string, unknown>, key: string): string | null {
	const value = record[key]
	return typeof value === 'string' && value.length > 0 ? value : null
}

/** An optional string field: absent reads as `undefined`, present-but-wrong-typed also reads as `undefined`. */
function optionalString(record: Record<string, unknown>, key: string): string | undefined {
	const value = record[key]
	return typeof value === 'string' && value.length > 0 ? value : undefined
}

/** An optional numeric field. A non-finite or non-numeric value is dropped rather than rendered. */
function optionalCount(record: Record<string, unknown>, key: string): number | undefined {
	const value = record[key]
	return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

const FILE_JOB_STATUSES = new Set<string>(Object.values(FileJobStatus))

/**
 * Whether the backend's `status` is one this build knows.
 *
 * An unrecognized value is never coerced to the nearest member: a status the
 * frontend cannot interpret must not be read as ready, failed or expired.
 */
function isFileJobStatus(value: unknown): value is FileJobStatus {
	return typeof value === 'string' && FILE_JOB_STATUSES.has(value)
}

/**
 * One listing row / per-request status body, or `null` if it cannot be trusted.
 *
 * @param {unknown} raw - The parsed response body, or one element of a listing.
 * @returns {ZipRequestSummary | null} The row, or null when a required field is missing or drifted.
 */
export function parseZipRequestSummary(raw: unknown): ZipRequestSummary | null {
	const record = asRecord(raw)
	if (!record) return null
	const requestId = requiredString(record, 'requestId')
	const jobId = requiredString(record, 'jobId')
	const fileName = requiredString(record, 'fileName')
	if (!requestId || !jobId || !fileName || !isFileJobStatus(record.status)) return null
	return {
		requestId,
		jobId,
		fileName,
		status: record.status,
		requestedCount: optionalCount(record, 'requestedCount'),
		includedCount: optionalCount(record, 'includedCount'),
		unavailableCount: optionalCount(record, 'unavailableCount'),
		createdAt: optionalString(record, 'createdAt'),
		completedAt: optionalString(record, 'completedAt'),
		resultExpiresAt: optionalString(record, 'resultExpiresAt'),
	}
}

/**
 * A `POST /files/jobs` response, or `null` if it cannot be trusted.
 *
 * @param {unknown} raw - The parsed creation response body.
 * @returns {ZipJobCreated | null} The accepted request, or null when the shape is unusable.
 */
export function parseZipJobCreated(raw: unknown): ZipJobCreated | null {
	const record = asRecord(raw)
	if (!record) return null
	const jobId = requiredString(record, 'jobId')
	const requestId = requiredString(record, 'requestId')
	if (!jobId || !requestId || !isFileJobStatus(record.status)) return null
	return { jobId, requestId, type: FileJobType.ZIP, status: record.status }
}

/**
 * A result body, or `null` if it cannot be trusted. The counts are required
 * here (unlike on a summary) because a result only exists once complete.
 *
 * @param {unknown} raw - The parsed result response body.
 * @returns {ZipResult | null} The result, or null when the shape is unusable.
 */
export function parseZipResult(raw: unknown): ZipResult | null {
	const record = asRecord(raw)
	if (!record) return null
	const requestId = requiredString(record, 'requestId')
	const downloadUrl = requiredString(record, 'downloadUrl')
	const fileName = requiredString(record, 'fileName')
	const requestedCount = optionalCount(record, 'requestedCount')
	const includedCount = optionalCount(record, 'includedCount')
	const unavailableCount = optionalCount(record, 'unavailableCount')
	if (!requestId || !downloadUrl || !fileName) return null
	if (requestedCount === undefined || includedCount === undefined || unavailableCount === undefined) return null
	return { requestId, downloadUrl, fileName, requestedCount, includedCount, unavailableCount }
}

/**
 * A UUID for one confirmation's creation attempt.
 *
 * `crypto.randomUUID` requires a secure context, exactly as `sha256Base64` in
 * `file.service.ts` does — the throw is deliberate, because a non-UUID key would
 * be rejected by the route with a far less obvious message.
 *
 * @returns {string} A v4 UUID, in the shape `z.uuid()` accepts.
 */
export function mintIdempotencyKey(): string {
	if (!globalThis.crypto?.randomUUID) {
		throw new Error('Preparing a ZIP requires a secure context (https or localhost).')
	}
	return crypto.randomUUID()
}

export const fileJobApi = {
	/**
	 * Submit one ZIP request for the given Files.
	 *
	 * The Library selection has no folder structure of its own, so every File
	 * lands in a single ungrouped group at the archive root. A caller that DOES
	 * have a packaging structure to state — a distribution package's own
	 * folders — uses {@link fileJobApi.create} instead.
	 *
	 * The body carries no `downloadFileName`: the archive's name is the
	 * backend's to choose, and nothing in this feature ever constructs one.
	 *
	 * @param {string[]} fileIds - The complete selection being packaged.
	 * @param {string} idempotencyKey - Stable for one confirmation; a replay resolves to the same request.
	 * @returns {Promise<ZipJobCreated>} The accepted request and its shared job's current status.
	 */
	createZipJob: async (fileIds: string[], idempotencyKey: string): Promise<ZipJobCreated> => {
		const response = await apiClient.post('/files/jobs', {
			type: FileJobType.ZIP,
			idempotencyKey,
			groups: [{ fileIds }],
		})
		const created = parseZipJobCreated(response.data)
		// An unreadable acceptance is treated as no acceptance: the caller's error
		// path preserves the selection, and durable work that did land is recovered
		// by the listing rather than guessed at from a shape nothing can read.
		if (!created) throw new Error('The ZIP request could not be confirmed.')
		return created
	},

	/**
	 * The current requester's useful/recoverable ZIP requests, newest first.
	 *
	 * A row that fails parsing is dropped and logged, never rendered partially —
	 * one drifted row must not blank the whole surface.
	 *
	 * @param {string} [cursor] - Opaque cursor from a previous page.
	 * @returns {Promise<{items: ZipRequestSummary[]; nextCursor?: string}>} One page of the listing.
	 */
	listZipRequests: async (cursor?: string): Promise<{ items: ZipRequestSummary[]; nextCursor?: string }> => {
		const response = await apiClient.get('/files/jobs', {
			params: { type: FileJobType.ZIP, ...(cursor ? { cursor } : {}) },
		})
		const body = asRecord(response.data)
		const rawItems = Array.isArray(body?.items) ? body.items : []
		const items: ZipRequestSummary[] = []
		for (const raw of rawItems) {
			const summary = parseZipRequestSummary(raw)
			if (summary) items.push(summary)
			else log.warn('Dropped an unreadable File Job listing row')
		}
		return { items, nextCursor: body ? optionalString(body, 'nextCursor') : undefined }
	},

	/**
	 * The authoritative state of one request.
	 *
	 * A `404` resolves to `not-found` because it is an answer, not a failure —
	 * the request is not addressable by this requester. Every other thrown error
	 * is re-thrown unchanged, so a transient failure keeps rejecting.
	 *
	 * @param {string} requestId - The request to read.
	 * @returns {Promise<FileJobStatusRead>} `found` / `not-found` / `unusable`.
	 */
	getZipRequest: async (requestId: string): Promise<FileJobStatusRead> => {
		let data: unknown
		try {
			const response = await apiClient.get(`/files/jobs/requests/${requestId}`)
			data = response.data
		} catch (error) {
			if (apiErrorStatus(error) === 404) return { kind: 'not-found' }
			throw error
		}
		const summary = parseZipRequestSummary(data)
		if (!summary) {
			log.warn('Unreadable File Job status body; leaving the request as it was')
			return { kind: 'unusable' }
		}
		return { kind: 'found', summary }
	},

	/**
	 * Mint a fresh download credential for a ready request.
	 *
	 * @param {string} requestId - The request whose result is wanted.
	 * @returns {Promise<ZipResult>} The freshly minted result.
	 */
	getZipResult: async (requestId: string): Promise<ZipResult> => {
		const response = await apiClient.get(`/files/jobs/requests/${requestId}/result`)
		const result = parseZipResult(response.data)
		if (!result) throw new Error('The download could not be prepared.')
		return result
	},

	/**
	 * Request an archive with an explicit packaging structure — directories and
	 * all. Returns as soon as the job is persisted and dispatched — never waits
	 * for the bytes.
	 */
	create: (body: CreateFileJobBody): Promise<FileJobCreated> =>
		apiClient.post<FileJobCreated>('/files/jobs', body).then((r) => r.data),

	/** One request's current status — the endpoint a caller polls while the worker runs. */
	get: (requestId: string): Promise<FileJobRequestSummary> =>
		apiClient.get<FileJobRequestSummary>(`/files/jobs/requests/${requestId}`).then((r) => r.data),

	/**
	 * The download credential for a READY request. 409s while the job is queued,
	 * processing, failed or expired, so it is only ever called after a READY status.
	 */
	result: (requestId: string): Promise<FileJobResult> =>
		apiClient.get<FileJobResult>(`/files/jobs/requests/${requestId}/result`).then((r) => r.data),
}

/**
 * The authoritative single-request read, shared by the session monitor and by
 * explicit download's "not yet usable" handling.
 *
 * Routed through `fetchQuery` on one key per request, so two callers asking the
 * same question at the same instant share one network call.
 *
 * @param {QueryClient} queryClient - The app's query client.
 * @param {string} requestId - The request to reconcile.
 * @returns {Promise<FileJobStatusRead>} The backend's own answer; rejects on a transient failure.
 */
export function reconcileFileJobRequest(queryClient: QueryClient, requestId: string): Promise<FileJobStatusRead> {
	return queryClient.fetchQuery({
		queryKey: fileJobRequestKey(requestId),
		queryFn: () => fileJobApi.getZipRequest(requestId),
		staleTime: 0,
	})
}

/** Refetch the useful-work listing — the one signal every surface uses to converge on a changed row. */
function invalidateZipList(queryClient: QueryClient): void {
	void queryClient.invalidateQueries({ queryKey: zipJobListKey() })
}

/**
 * Report a `409` — the request is still addressable, so its own current state
 * decides the copy. No branch starts a download.
 */
async function reportNotReady(queryClient: QueryClient, requestId: string): Promise<void> {
	let read: FileJobStatusRead
	try {
		read = await reconcileFileJobRequest(queryClient, requestId)
	} catch {
		notification.error('Could not download this ZIP. Please try again.')
		return
	}
	if (read.kind === 'not-found') {
		reportGone(queryClient)
		return
	}
	invalidateZipList(queryClient)
	if (read.kind === 'unusable') {
		notification.error('Could not download this ZIP. Please try again.')
		return
	}
	const status = read.summary.status
	if (status === FileJobStatus.QUEUED || status === FileJobStatus.PROCESSING) {
		notification.info('This ZIP is still preparing.')
		return
	}
	if (status === FileJobStatus.READY) {
		notification.error('Could not download this ZIP. Please try again.')
		return
	}
	notification.error('This ZIP is no longer downloadable.')
}

/**
 * Report a result that is no longer addressable. No further per-request read is
 * attempted: the status endpoint applies the identical tenant/requester scoping
 * and would answer the same way.
 */
function reportGone(queryClient: QueryClient): void {
	invalidateZipList(queryClient)
	notification.error('This ZIP is no longer available.')
}

/**
 * Mint a fresh credential and hand it straight to the browser.
 *
 * The credential is held in no variable that outlives this call, is never
 * returned to the caller, and is never written to the query cache — every press
 * mints its own. Called identically from a Recent downloads row and from a
 * terminal notification's action.
 *
 * @param {QueryClient} queryClient - Used to reconcile the row when the download is refused.
 * @param {string} requestId - The request to download.
 */
export async function downloadZipResult(queryClient: QueryClient, requestId: string): Promise<void> {
	try {
		const result = await fileJobApi.getZipResult(requestId)
		triggerBrowserDownload(result.downloadUrl)
	} catch (error) {
		const status = apiErrorStatus(error)
		if (status === 409) return reportNotReady(queryClient, requestId)
		if (status === 404) return reportGone(queryClient)
		notification.error('Could not download this ZIP. Please try again.')
	}
}
